import axios from 'axios';
import getBaseUrl from '../../../shared/utils/Host';

const BaseUrl = getBaseUrl();
const API_URL = `${BaseUrl}order`; // Base URL untuk order

const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return {
        headers: {
            Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
    };
};

export const orderService = {
    getPaginateAdmin: async (params = {}) => {
        try {
            const defaultParams = {
                TypeProduct: 'PRODUCT',
                limit: 10,
                page: 1,
                ...params,
            };
            const queryString = new URLSearchParams(defaultParams).toString();
            const response = await axios.get(
                `${API_URL}/order/paginate-admin?${queryString}`,
                getAuthHeader()
            );
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || 'Gagal mengambil data order',
                status: error.response?.status,
                data: error.response?.data,
            };
        }
    },

    /**
     * 🎯 Ambil SEMUA data order dengan loop pagination — VERSI ROBUST
     *
     * Perbaikan dari versi sebelumnya:
     * 1. Deteksi limit AKTUAL yang dikembalikan server
     *    (misal kita minta 100, server cap 50 → actualLimit = 50)
     * 2. Baca `meta.total` dari response kalau ada → stop saat total tercapai
     * 3. Cek duplikat by `id` → hindari infinite loop kalau server balikin
     *    data yang sama berulang
     * 4. Stop kalau batch kosong
     * 5. Safety `MAX_PAGES` = 500 (max 50.000 baris)
     */
    getAllPaginateAdmin: async (extraParams = {}) => {
        const REQUESTED_LIMIT = 100; // minta 100, tapi server bisa cap lebih kecil
        const MAX_PAGES = 500;
        const seenIds = new Set();

        let allData = [];
        let page = 1;
        let hasMore = true;
        let totalFromServer = null;
        let actualLimit = null;

        try {
            while (hasMore && page <= MAX_PAGES) {
                const params = {
                    page,
                    limit: REQUESTED_LIMIT,
                    ...extraParams,
                };

                // Bersihkan param undefined/null/string kosong
                const cleanParams = Object.fromEntries(
                    Object.entries(params).filter(
                        ([, v]) => v !== undefined && v !== null && v !== ''
                    )
                );

                const queryString = new URLSearchParams(cleanParams).toString();
                const response = await axios.get(
                    `${API_URL}/order/paginate-admin?${queryString}`,
                    getAuthHeader()
                );

                const responseData = response.data;

                // Support beberapa bentuk response umum
                const batch =
                    responseData?.data ??
                    responseData?.items ??
                    responseData?.orders ??
                    (Array.isArray(responseData) ? responseData : []);

                // ─────────────────────────────────────────────
                // 1) Deteksi limit AKTUAL dari server
                //    Kalau kita minta 100 tapi server balikin 50,
                //    maka actualLimit = 50
                // ─────────────────────────────────────────────
                if (actualLimit === null && batch.length > 0) {
                    actualLimit =
                        batch.length === REQUESTED_LIMIT
                            ? REQUESTED_LIMIT
                            : batch.length;
                    console.log(
                        `🔧 [Export] Limit aktual server: ${actualLimit} baris/halaman`
                    );
                }

                // ─────────────────────────────────────────────
                // 2) Baca total dari meta (kalau ada)
                // ─────────────────────────────────────────────
                const meta = responseData?.meta ?? responseData?.pagination ?? {};
                const total =
                    meta.total ??
                    meta.totalItems ??
                    meta.total_items ??
                    meta.count ??
                    responseData?.total ??
                    responseData?.totalItems ??
                    null;

                if (total !== null && totalFromServer === null) {
                    totalFromServer = Number(total);
                    console.log(
                        `🔧 [Export] Total di server: ${totalFromServer} baris`
                    );
                }

                console.log(
                    `  📄 [Export] Page ${page}: ${batch.length} baris (total terkumpul: ${allData.length})`
                );

                // ─────────────────────────────────────────────
                // 3) Batch kosong = sudah tidak ada data
                // ─────────────────────────────────────────────
                if (batch.length === 0) {
                    hasMore = false;
                    break;
                }

                // ─────────────────────────────────────────────
                // 4) Filter duplikat by ID
                // ─────────────────────────────────────────────
                let newItems = 0;
                for (const item of batch) {
                    const id =
                        item?.id ??
                        item?._id ??
                        item?.invoice_id ??
                        JSON.stringify(item);
                    if (!seenIds.has(id)) {
                        seenIds.add(id);
                        allData.push(item);
                        newItems++;
                    }
                }

                // Semua duplikat → server sudah balikin data yang sama → stop
                if (newItems === 0) {
                    console.warn(
                        '⚠️ [Export] Semua item duplikat, stop untuk hindari infinite loop.'
                    );
                    hasMore = false;
                    break;
                }

                // ─────────────────────────────────────────────
                // 5) Sudah dapat semua data (sesuai meta.total)
                // ─────────────────────────────────────────────
                if (totalFromServer !== null && allData.length >= totalFromServer) {
                    console.log(
                        `✅ [Export] Total ${totalFromServer} baris sudah tercapai.`
                    );
                    hasMore = false;
                    break;
                }

                // ─────────────────────────────────────────────
                // 6) Batch < actualLimit → halaman terakhir
                // ─────────────────────────────────────────────
                if (actualLimit !== null && batch.length < actualLimit) {
                    console.log(
                        `✅ [Export] Halaman terakhir (${batch.length} < ${actualLimit}).`
                    );
                    hasMore = false;
                    break;
                }

                page += 1;
            }

            console.log(
                `🎉 [Export] Selesai. Total: ${allData.length} baris dari ${page} halaman.`
            );
            return { success: true, data: allData };
        } catch (error) {
            console.error('❌ [Export] Error:', error);
            return {
                success: false,
                error:
                    error.response?.data?.message ||
                    'Gagal mengambil semua data order',
                status: error.response?.status,
                data: error.response?.data,
            };
        }
    },
};

// Untuk kompatibilitas dengan kode lama
export const getOrder = async () => {
    const token = localStorage.getItem('token');
    return axios.get(`${API_URL}`, {
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
        },
    });
};

// Export fungsi paginate-admin secara langsung jika diperlukan
export const getOrdersPaginateAdmin = (params) => {
    return orderService.getPaginateAdmin(params);
};